import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { DateFieldComponent } from '@custom-components/general/form/date-field/date-field.component';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment-timezone';

// The instant a binding change takes effect, entered as a wall-clock date and time in the
// installation's zone and kept as a UTC ISO instant. The native Dates behind the two pickers carry
// only that wall clock; the browser's own zone never enters the value.
@Component({
  selector: 'xemex-effective-from-field',
  imports: [
    DateFieldComponent,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTimepickerModule,
    TranslateModule,
  ],
  providers: [provideNativeDateAdapter()],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './effective-from-field.component.html',
  styleUrl: './effective-from-field.component.scss',
})
export class EffectiveFromFieldComponent {
  readonly instant = model.required<string>();
  readonly timeZone = input.required<string>();
  // A translation key.
  readonly error = input<string | null>(null);
  // A short form of the rule, beside the label; without one the full rule is spelled out below.
  readonly hint = input<string | null>(null);
  readonly ruleBroken = input(false);

  private readonly wallClock = computed(() => moment.utc(this.instant()).tz(this.timeZone()));
  protected readonly date = computed(() => {
    const wallClock = this.wallClock();
    return new Date(wallClock.year(), wallClock.month(), wallClock.date());
  });
  protected readonly time = computed(() => {
    const wallClock = this.wallClock();
    return new Date(2000, 0, 1, wallClock.hour(), wallClock.minute());
  });

  protected onDate(date: Date | null): void {
    if (date) this.instant.set(this.toInstant(date, this.time()));
  }

  // A half-typed time parses to null; the instant keeps its last whole value until it parses.
  protected onTime(time: Date | null): void {
    if (time) this.instant.set(this.toInstant(this.date(), time));
  }

  private toInstant(date: Date, time: Date): string {
    return moment
      .tz(
        {
          year: date.getFullYear(),
          month: date.getMonth(),
          date: date.getDate(),
          hour: time.getHours(),
          minute: time.getMinutes(),
        },
        this.timeZone(),
      )
      .utc()
      .toISOString();
  }
}
